import { useContext } from "react";
import { MainContext } from "../../../main/context/MainContextProvider";
import { Typography, Divider } from "@mui/material";
import ReactDOMServer from "react-dom/server";
import TreeChartTooltip from "./TreeChartTooltip";
import { RopChart } from "../../../main/components/RopChart";
export type TreeChartData = {
  name: string;
  tooltipItens?: {
    name: string;
    value: string | number | undefined;
  }[];
  children?: TreeChartData[];
};
export type TreeChartProps = {
  cardId?: string;
  title?: string;
  data: TreeChartData;
  headerItems?: {
    name: string;
    value: string | number | undefined;
  }[];
};

export default function TreeChart(props: TreeChartProps) {
  const data = props.data;
  const { theme } = useContext(MainContext);
  const option = {
    backgroundColor: theme.cardsDefaultBgColor,

    tooltip: {
      trigger: "item",
      triggerOn: "mousemove",
      position: (
        point: any,
        _params: any,
        _dom: any,
        _rect: any,
        size: any
      ) => {
        const sv = size.viewSize;
        const sc = size.contentSize;
        const posX =
          point[0] < sv[0] / 2
            ? point[0] + sc[0] / 3.6
            : point[0] - sc[0] * 1.2;
        const posY = point[1] > sv[1] ? point[1] - sc[1] / 2 : point[1];
        return [posX, posY];
      },
      formatter: (v: any) => {
        if (v.data.tooltipItens) {
          const tooltipItens: {
            name: string;
            value: string | number | undefined;
          }[] = v.data.tooltipItens;
          const jsxTooltipContent = <TreeChartTooltip items={tooltipItens} />;
          const plainHtml = ReactDOMServer.renderToString(jsxTooltipContent);

          return plainHtml;
        }
      },
    },
    series: [
      {
        type: "tree",

        data: [data],
        top: "1%",
        left: "15%",
        bottom: "1%",
        right: "20%",

        symbolSize: 7,

        label: {
          position: "left",
          verticalAlign: "middle",
          align: "right",
          fontSize: 9,
          formatter: (v: any) => {
            const nodeStyles = [
              `{nodeBg| ${
                v.name.length > 14 ? v.name.substring(0, 12) + "..." : v.name
              }}`,
            ].join(" ");
            const leafStyles = [
              `{leafBg| ${
                v.name.length > 14 ? v.name.substring(0, 12) + "..." : v.name
              }}`,
            ].join(" ");
            const styles = !!v.data.children ? nodeStyles : leafStyles;
            return styles;
          },
          rich: {
            valueBg: {
              backgroundColor: "rgba(0,23,11,0.3)",
              align: "center",
              verticalAlign: "middle",
              borderRadius: 4,
              width: 26,
              padding: [4, 8],
            },
            nodeBg: {
              backgroundColor: "rgb(51, 153, 255)",
              align: "center",
              minWidth: 60,
              padding: 12,
              borderRadius: 12,
              shadowBlur: 12,
              shadowOffsetX: 2,
              shadowOffsetY: 2,
              shadowColor: "#aaa",
            },
            leafBg: {
              backgroundColor: "rgb(153, 204, 255)",
              align: "center",
              minWidth: 60,
              padding: 12,
              borderRadius: 12,
              shadowBlur: 12,
              shadowOffsetX: 2,
              shadowOffsetY: 2,
              shadowColor: "#aaa",
            },
          },
        },

        leaves: {
          label: {
            position: "right",
            verticalAlign: "middle",
            align: "left",
          },
        },

        emphasis: {
          focus: "descendant",
        },

        expandAndCollapse: true,
        animationDuration: 550,
        animationDurationUpdate: 750,
      },
    ],
  };
  return (
    <>
      <div className="text-start mb-2 w-100">
        <div
          style={{ minHeight: "32px" }}
          className="d-flex justify-content-between w-100 align-items-center"
        >
          <Typography marginBottom={1} color={theme.primaryTextColor}>
            {props.title}
          </Typography>
          <div className="d-flex flex-row justify-content-around">
            {props.headerItems?.map((item, index) => (
              <Typography
                sx={{
                  fontSize: "0.8rem",
                  fontWeight: "bold",
                  marginRight: "8px",
                }}
                key={index}
                color={theme.primaryTextColor}
              >
                {item.name}: {item.value}
              </Typography>
            ))}
          </div>
        </div>
        <Divider
          variant="fullWidth"
          style={{ backgroundColor: theme.muiTheme.palette.secondary.main }}
        />
      </div>

      <div
        style={{
          width: "100%",
          height: "100%",
        }}
      >
        <RopChart option={option} />
      </div>
    </>
  );
}
